// src/features/streams/schemas/ScheduleForm.schema.ts

import { z } from 'zod';
import {
  SCHEDULE_REPEAT_OPTIONS,
  SCHEDULE_INTERVAL_MAX,
  SCHEDULE_INTERVAL_UNIT_LABELS,
  STREAM_SCHEDULE_CONSTRAINTS,
} from '@/lib/constants/stream';
import { StreamScheduleUtils } from '../StreamScheduleUtils';

/**
 * Schema factory: refinements convert the picker strings with the schedule
 * zone (NOT browser-local), so the schema is built per modal open with the
 * stream's zoneId and a fixed nowSecs.
 */
export function makeScheduleFormSchema(zoneId: string, nowSecs: number) {
  return z.object({
    startStr: z.string().min(1, 'Start time is required'),   // 'yyyy-MM-dd HH:mm:ss'
    endStr: z.string().min(1, 'End time is required'),
    repeats: z.enum(SCHEDULE_REPEAT_OPTIONS),
    interval: z.number().int().min(1, 'Repeat interval must be at least 1'),
    overrideTitle: z.boolean(),
    title: z.string(),
    overrideDesc: z.boolean(),
    desc: z.string().max(
      STREAM_SCHEDULE_CONSTRAINTS.DESC_MAX,
      `Max ${STREAM_SCHEDULE_CONSTRAINTS.DESC_MAX} characters`,
    ),
    overrideTags: z.boolean(),
    tags: z.array(z.string()),
  }).superRefine((data, ctx) => {
    const start = StreamScheduleUtils.dateStrToUtcSeconds(data.startStr, zoneId);
    const end = StreamScheduleUtils.dateStrToUtcSeconds(data.endStr, zoneId);

    if (Number.isFinite(start) && Number.isFinite(end)) {
      if (end <= start) {
        ctx.addIssue({ code: 'custom', path: ['endStr'], message: 'End time must be after start time' });
      } else if (end - start > STREAM_SCHEDULE_CONSTRAINTS.MAX_DURATION_HOURS * 3600) {
        ctx.addIssue({
          code: 'custom',
          path: ['endStr'],
          message: `Schedule duration must be ${STREAM_SCHEDULE_CONSTRAINTS.MAX_DURATION_HOURS} hours or less`,
        });
      }
      if (data.repeats === 'never' && end <= nowSecs) {
        ctx.addIssue({
          code: 'custom',
          path: ['endStr'],
          message: 'This schedule would already be expired — choose a future time',
        });
      }
      if (start > nowSecs + STREAM_SCHEDULE_CONSTRAINTS.MAX_FUTURE_DAYS * 86400) {
        ctx.addIssue({
          code: 'custom',
          path: ['startStr'],
          message: 'Start time must be within the next 13 months',
        });
      }
    }

    if (data.repeats !== 'never' && data.interval > SCHEDULE_INTERVAL_MAX[data.repeats]) {
      ctx.addIssue({
        code: 'custom',
        path: ['interval'],
        message: `Must be between 1 and ${SCHEDULE_INTERVAL_MAX[data.repeats]} ${SCHEDULE_INTERVAL_UNIT_LABELS[data.repeats]}s`,
      });
    }

    if (data.overrideTitle) {
      const title = data.title.trim();
      if (title.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['title'], message: 'Title is required when overriding' });
      } else if (title.length > STREAM_SCHEDULE_CONSTRAINTS.TITLE_MAX) {
        ctx.addIssue({
          code: 'custom',
          path: ['title'],
          message: `Max ${STREAM_SCHEDULE_CONSTRAINTS.TITLE_MAX} characters`,
        });
      }
    }
  });
}

export type ScheduleFormData = z.infer<ReturnType<typeof makeScheduleFormSchema>>;
